import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { css, ThemeContext } from '@emotion/react';

import { AppTheme } from 'theme';

import { Button } from 'components';
import UnfinishedTutorialDialog from 'components/UserBanner/UnfinishedTutorialDialog';

import { GameDifficulty, GameTypeEnum } from 'graphql/types';
import colors from 'lib/colors';

import { ScenesProgressContext } from 'pages/Lobby/ScenesProgressProvider';
import { useStartTutorial } from 'pages/useStartTutorial';

interface Props {
  disabled: boolean;
  creating: boolean;
  gameType: GameTypeEnum;
  selectedSceneId?: string;
  startCpuGame: () => void;
  handleStartMatchmaking: () => void;
  gameDifficulty: GameDifficulty;
  isMediumGameActive?: boolean;
}

const StartGameButtons = ({
  handleStartMatchmaking,
  startCpuGame,
  disabled,
  creating,
  gameType,
  selectedSceneId,
  gameDifficulty,
  isMediumGameActive,
}: Props) => {
  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;

  const [t] = useTranslation();

  const { trainingRequirements, allScenesCompleted, trainingScenes } =
    useContext(ScenesProgressContext);

  const {
    unfinishedTutorial,
    startingTutorial,
    creatingTutorial,
    continueTutorial,
    restartTutorial,
    handleStartTutorial,
    closeUnfinishedTutorialDialog,
  } = useStartTutorial();

  let againstPlayerLabelT = {
    key: 'lobby.trainWithPlayer',
    defaultValue: 'Trénovat s kolegou',
  };
  let againstCpuLabelT = isMediumGameActive
    ? {
      key: 'lobby.trainWithCpu',
      defaultValue: 'Trénovat s počítačem',
    }
    : {
      key: 'lobby.playScene',
      defaultValue: 'Spustit dějiště',
    };
  let againstPlayerFirst = true;
  const isEasyGame = gameDifficulty === GameDifficulty.EASY;

  if (
    gameType === GameTypeEnum.TRAINING_GAME &&
    trainingRequirements &&
    !allScenesCompleted
  ) {
    const { finishAgainstPlayer } = trainingRequirements;
    const scene = trainingScenes.find((ts) => ts.id === selectedSceneId);
    const sceneInCompleted = !scene?.progress?.alreadyFinished;

    if (selectedSceneId == null || sceneInCompleted) {
      againstCpuLabelT = isMediumGameActive
        ? {
          key: 'lobby.playWithCpu',
          defaultValue: 'Hrát s počítačem',
        }
        : {
          key: 'lobby.playTrainingScene',
          defaultValue: 'Spustit školení',
        };

      if (finishAgainstPlayer) {
        againstPlayerLabelT = {
          key: 'lobby.playWithPlayer',
          defaultValue: 'Hrát s kolegou',
        };
      }
    }

    if (!finishAgainstPlayer) {
      againstPlayerFirst = false;
    }
  }

  const isTournament = gameType === GameTypeEnum.TOURNAMENT_GAME;
  if (isTournament) {
    againstPlayerLabelT = {
      key: 'lobby.tournament.playWithPlayer',
      defaultValue: 'Vyzvat náhodného kolegu',
    };
  }

  function getContainerCss(gameType: GameTypeEnum) {
    if (gameType === GameTypeEnum.TOURNAMENT_GAME) {
      return css``;
    } else {
      return isMediumGameActive
        ? css`
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          column-gap: 8px;
          grid-template-areas: 'first second third';
        `
        : css`
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          column-gap: 8px;
          grid-template-areas: 'second third';
        `;
    }
  }

  const stylesFirstButton = css`
    grid-column: 1;
    grid-area: first;

    &:hover {
      filter: brightness(110%);
    }
  `;

  const stylesFirstButtonDisabled = css`
    grid-column: 1;
    grid-area: first;
    background: ${colors.gray};
    color: ${colors.disabledInputText};
  `;

  const stylesSecondButton = css`
    grid-column: 2;
    background: none;
    ${!IS_EDU ? `background-color: ${colors.blueBackground};` : ''}
    color: ${IS_EDU ? colors.lobbyOrange : colors.warning};
    border: 2px solid ${IS_EDU ? colors.lobbyOrange : colors.warning};
    padding: 12px 32px;
    grid-area: second;

    &:hover {
      filter: brightness(110%);
    }
  `;

  const stylesThirdButton = css`
    grid-column: 3;
    grid-row: 1;
    background: none;
    ${!IS_EDU ? `background-color: ${colors.blueBackground};` : ''}
    color: ${colors.white};
    border: 2px solid ${colors.white};
    padding: 12px 32px;
    grid-area: third;
    &:hover {
      filter: brightness(110%);
    }
  `;

  const buttonDisabled =
    disabled || creating || startingTutorial || creatingTutorial;

  const containerCss = getContainerCss(gameType);
  return (
    <>
      <div css={containerCss}>
        {isMediumGameActive && <Button
          labelT={againstPlayerLabelT}
          onClick={() => {
            handleStartMatchmaking();
          }}
          disabled={buttonDisabled || isEasyGame}
          variant={againstPlayerFirst ? 'warning' : undefined}
          tooltip={
            isEasyGame
              ? t(
                'lobby.game.notAvailableOnEasyGame',
                'Dostupné pouze v pokročilé hře',
              )
              : null
          }
          css={
            isEasyGame
              ? stylesFirstButtonDisabled
              : againstPlayerFirst
                ? stylesFirstButton
                : stylesSecondButton
          }
        />
        }
        {!isTournament && (
          <Button
            labelT={againstCpuLabelT}
            disabled={buttonDisabled}
            onClick={startCpuGame}
            variant={!againstPlayerFirst ? 'warning' : undefined}
            css={
              !againstPlayerFirst && !isEasyGame
                ? stylesFirstButton
                : stylesSecondButton
            }
          />
        )}
        {!IS_EDU && !isTournament && (
          <Button
            labelT={
              isEasyGame
                ? {
                  key: 'userBanner.menu.startTutorial',
                  defaultValue: 'Spustit hru s průvodcem',
                }
                : {
                  key: 'userBanner.menu.explainMediumGame',
                  defaultValue: 'Vysvětlit pokročilou hru',
                }
            }
            disabled={buttonDisabled}
            onClick={handleStartTutorial}
            variant={'warningFaded'}
            css={stylesThirdButton}
          />
        )}
      </div>
      <UnfinishedTutorialDialog
        isOpen={unfinishedTutorial != null}
        onClose={closeUnfinishedTutorialDialog}
        onContinue={continueTutorial}
        onRestart={restartTutorial}
      />
    </>
  );
};

export default StartGameButtons;
